/**
 * useProfileHeaderProps Hook
 *
 * Helper hook to transform profile data into ProfileHeader props.
 * This simplifies usage in parent components while keeping ProfileHeader presentational.
 */

import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import type { ContractorProfile, Locale } from '@hummii/shared-types';
import type { ProfileViewType } from '../types';
import type { ProfileHeaderProps } from '../ui/ProfileHeader';
import type { BreadcrumbItem } from '@/src/shared/ui/navigation/Breadcrumb';
import { getRatingStats } from '../lib/ratingUtils';

/**
 * A client profile comes from a different endpoint and is flat: the name and the
 * avatar sit on the object itself, there is no nested `user`. Declared here rather
 * than imported from the features layer, so entities stays free of that dependency.
 */
export interface ClientHeaderProfile {
  role: 'client';
  firstName?: string | null;
  lastName?: string | null;
  avatar?: string | null;
  city?: string | null;
  bio?: string | null;
}

export type ProfileHeaderSource = ContractorProfile | ClientHeaderProfile;

function isClientProfile(profile: ProfileHeaderSource): profile is ClientHeaderProfile {
  return (profile as ClientHeaderProfile).role === 'client';
}

export interface UseProfileHeaderPropsOptions {
  profile: ProfileHeaderSource | null | undefined;
  viewType: ProfileViewType;
  isCompact?: boolean;
  activeTab?: 'overview' | 'orders' | 'messages' | 'settings';
  onTabChange?: (tab: 'overview' | 'orders' | 'messages' | 'settings') => void;
  onReportClick?: () => void;
  onMessageClick?: () => void;
}

/**
 * Hook to prepare ProfileHeader props from contractor profile data
 *
 * @example
 * ```tsx
 * const { data: profile } = useContractorProfile({ userId });
 * const headerProps = useProfileHeaderProps({
 *   profile,
 *   viewType: 'contractor-own',
 *   isCompact,
 *   activeTab,
 *   onTabChange
 * });
 *
 * return <ProfileHeader {...headerProps} />;
 * ```
 */
export function useProfileHeaderProps({
  profile,
  viewType,
  isCompact = false,
  activeTab = 'overview',
  onTabChange,
  onReportClick,
  onMessageClick,
}: UseProfileHeaderPropsOptions): ProfileHeaderProps | null {
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  const t = useTranslations('profile');
  const locale = useLocale() as Locale;
  const router = useRouter();

  // Early return AFTER all hooks are called
  if (!profile) {
    return null;
  }

  // Narrowed once, so every field below is read from the right shape.
  const clientProfile = isClientProfile(profile) ? profile : null;
  const contractorProfile = isClientProfile(profile) ? null : profile;

  const { rating, reviewCount } = getRatingStats(contractorProfile);

  // Determine badge based on rating
  const badge = rating && rating >= 4.5
    ? t('badge.topTasker')
    : undefined;

  // Social links are not yet stored on the backend User/Actor model.
  const socialLinks = undefined;

  const name = clientProfile
    ? [clientProfile.firstName, clientProfile.lastName].filter(Boolean).join(' ')
    : contractorProfile?.user?.name || '';

  // Only a public contractor profile is reached from the catalogue, so only it
  // gets the full trail; own and client profiles show the name alone.
  const breadcrumbItems: BreadcrumbItem[] = [];

  if (viewType === 'contractor-public') {
    breadcrumbItems.push({
      label: t('breadcrumb.categories'),
      onClick: () => router.push(`/${locale}/categories`),
    });

    const primaryCategory = contractorProfile?.categories?.[0]?.category;

    if (primaryCategory) {
      breadcrumbItems.push({
        label:
          locale === 'fr'
            ? primaryCategory.nameFr || primaryCategory.nameEn
            : primaryCategory.nameEn,
        onClick: () => router.push(`/${locale}/search?category=${primaryCategory.slug}`),
      });
    }
  }

  if (name) {
    breadcrumbItems.push({ label: name, isActive: true });
  }

  return {
    name,
    avatarUrl: clientProfile
      ? clientProfile.avatar ?? null
      : contractorProfile?.user?.avatarUrl ?? null,
    badge,
    city: profile.city ?? null,
    // Postal code, completed-task count and availability are contractor-only:
    // a client profile carries none of them.
    postalCode: contractorProfile?.postalCode,
    rating,
    reviewsCount: reviewCount,
    tasksCompleted: contractorProfile?.completedOrders,
    viewType,
    isOnline: contractorProfile?.isAvailable,
    // No response-time metric is tracked on the backend yet, so it stays hidden.
    responseTime: undefined,
    bio: profile.bio,
    socialLinks,
    isCompact,
    activeTab,
    onTabChange,
    onReportClick,
    onMessageClick,
    breadcrumbItems,
  };
}
